import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * ⚠️ Chaque variante à FOND porte un état `active:`.
 *
 * Tailwind 3.4 est configuré SANS `future.hoverOnlyWhenSupported` : `hover:`
 * compile en `:hover` nu, qu'iOS applique au toucher — en retard, puis LAISSE
 * COLLÉ après le relâchement. Le doigt n'obtient donc aucun retour au moment de
 * l'appui, et un bouton reste allumé une fois quitté.
 *
 * Le retour passe par le FOND et non par un `active:scale` sur la base : un
 * `scale` frapperait aussi la variante `link` (sans fond) et tous les boutons
 * `w-full`, où un mouvement de 4 à 12 px se lit comme un défaut d'affichage.
 */
const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80",
                destructive:
                    "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80",
                // DS §19.8 — action destructive DISCRÈTE (corbeille de ligne, « Supprimer »
                // secondaire). Neutre au repos, fond rose au survol : sans ce fond, le bouton
                // se survole en GRIS et rien ne distingue « supprimer » de « modifier ».
                // Le rappeler à chaque écran ne marche pas — c'était oublié 1 fois sur 3.
                "destructive-ghost":
                    "text-muted-foreground hover:bg-destructive/10 active:bg-destructive/20 hover:text-destructive focus-visible:text-destructive",
                "destructive-outline":
                    "border border-destructive/40 text-destructive hover:bg-destructive/10 active:bg-destructive/20 hover:text-destructive",
                outline:
                    "border border-input bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent/80",
                secondary:
                    "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70",
                ghost: "hover:bg-accent hover:text-accent-foreground active:bg-accent/80",
                link: "text-primary underline-offset-4 hover:underline",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        )
    }
)
Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
